import { fetchLaborCrewsForRegion, fetchRegions } from "../../api";
import { ValidationError } from "../../errors";
import { LaborCrewRow, transformRegionalLaborCrews } from "../datasources/laborCrews";
import { loadDataIntoSheet } from "../workbook";

/** A `__LaborCrews` row, after a `region` column naming whose makeup and rates it holds. */
type RegionalLaborCrewRow = { region: string } & LaborCrewRow;

const fetchRegionRows = async (
  regionId: string,
  regionName: string,
  options: { skipEmptyCrews: boolean }
): Promise<RegionalLaborCrewRow[]> => {
  const result = await fetchLaborCrewsForRegion(regionId);
  if (!result.success) {
    throw result.error;
  }
  const crews = (result.data || []).filter(
    (crew) => !options.skipEmptyCrews || (crew.labor_crew_resource_quantities ?? []).length > 0
  );
  return transformRegionalLaborCrews(crews).map((row) => ({ region: regionName, ...row }));
};

/**
 * Every live region, by name. A failed region fails the load, so the sheet never quietly misses one.
 */
const fetchAllRegionRows = async (): Promise<RegionalLaborCrewRow[]> => {
  const regionsResult = await fetchRegions();
  if (!regionsResult.success) {
    throw regionsResult.error;
  }
  const regions = (regionsResult.data || [])
    .filter((region) => region.deleted_at == null)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  const rowsByRegion = await Promise.all(
    regions.map((region) => fetchRegionRows(region.id, region.name ?? "", { skipEmptyCrews: true }))
  );
  return rowsByRegion.flat();
};

/**
 * Writes the UPC catalog crews, with their makeup and blended rates, to `admin_UPCLaborCrews`. The
 * columns match `__LaborCrews`, after a `region` column, so a formula built on one table works on
 * the other once it also matches on `region`.
 *
 * With the project's region, that region's crews are listed, an empty crew as one blank-member row
 * as in `__LaborCrews`. Without one, every region's are, region by region, and a crew with no makeup
 * in a region is left out of it: makeup is set per region, and most crews are set up in only some.
 */
export const tabulateUPCLaborCrews = async (regionId?: string, regionName?: string): Promise<void> => {
  const rows = regionId
    ? await fetchRegionRows(regionId, regionName ?? "", { skipEmptyCrews: false })
    : await fetchAllRegionRows();
  if (rows.length === 0) {
    throw new ValidationError("No UPC labor crews found");
  }
  await loadDataIntoSheet(rows, "UPCLaborCrews", { adminTable: true });
};
